import * as React from 'react';
import { memo, useCallback } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { ZoomRecordingFieldsFragment } from 'core/graphql';
import { ZOOM_RECORDING_FIELDS } from 'core/operations/fragments/zoom';
import { useZoomMutations, usePopupStateOperations } from 'core/hooks';
import { formatWarsaw } from 'core/utils/zoomTime';
import { formatBytes, parseZoomFiles } from 'core/utils/format';
import { Colors } from 'services/ColorService';
import { getFont } from 'services/FontService';

type RecordingState = 'orphan' | 'matched' | 'deleted';

const STATE_COLORS: Record<RecordingState, { bg: string; fg: string }> = {
  orphan: { bg: '#FEF3D8', fg: '#9A6B05' },
  matched: { bg: '#DDF3DD', fg: '#1F7A1F' },
  deleted: { bg: '#FDE3E3', fg: '#B42323' },
};

/**
 * Raw ZoomRecording card. The schema has no single-recording query, so the
 * record is read from the Apollo cache (populated by the recordings list).
 * A deep link to an uncached recording redirects back to the list.
 */
export const RecordingCard = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const client = useApolloClient();
  const mutations = useZoomMutations();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const recording = client.cache.readFragment<ZoomRecordingFieldsFragment>({
    id: client.cache.identify({ __typename: 'ZoomRecordingType', id }),
    fragment: ZOOM_RECORDING_FIELDS,
    fragmentName: 'ZoomRecordingFields',
  });

  const onDelete = useCallback(() => {
    if (!recording) {
      return;
    }
    const confirmString = `DELETE ${recording.pk}`;
    showPopup(PopupNames.zoomDangerConfirm, {
      title: `${t('pages.zoom.recordings.deleteTitle')}`,
      body: `${t('pages.zoom.recordings.deleteBody')}`,
      confirmString,
      onConfirm: async () => {
        await mutations.deleteRecording.run(recording.id, confirmString);
        history.push(PATHS.zoomRecordings);
      },
    });
  }, [recording, showPopup, mutations, history, t]);

  if (!recording) {
    return <Redirect to={PATHS.zoomRecordings} />;
  }

  const files = parseZoomFiles(recording.files);
  let state: RecordingState | null = null;
  if (recording.deletedFromZoom) {
    state = 'deleted';
  } else if (recording.isMatched) {
    state = 'matched';
  } else if (recording.isOrphan) {
    state = 'orphan';
  }

  return (
    <Wrapper>
      <BackLink
        type="button"
        onClick={() => history.push(PATHS.zoomRecordings)}
      >
        ← {t('pages.zoom.recordings.back')}
      </BackLink>

      <HeaderPanel>
        <HeaderMain>
          <Title>
            {recording.topic || t('pages.zoom.recordings.cardTitle')}
          </Title>
          <SubTitle>
            {recording.zoomAccount?.displayName}
            {recording.zoomAccount?.email
              ? ` · ${recording.zoomAccount.email}`
              : ''}
          </SubTitle>
          <ChipRow>
            {state ? (
              <StateChip state={state}>
                {t(`pages.zoom.recordings.state.${state}`)}
              </StateChip>
            ) : null}
            {recording.isTooShort ? (
              <TooShortChip>
                {t('pages.zoom.recordings.state.tooShort')}
              </TooShortChip>
            ) : null}
          </ChipRow>
        </HeaderMain>
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={recording.deletedFromZoom}
          onClick={onDelete}
        >
          {t('pages.zoom.recordings.actions.delete')}
        </Button>
      </HeaderPanel>

      <Panel>
        <PanelTitle>{t('pages.zoom.recordings.metadata')}</PanelTitle>
        <MetaGrid>
          <Meta label={t('pages.zoom.recordings.columns.start')}>
            {formatWarsaw(recording.startTime)}
          </Meta>
          <Meta label={t('pages.zoom.recordings.columns.account')}>
            {recording.zoomAccount?.displayName}
            {recording.zoomAccount?.email
              ? ` · ${recording.zoomAccount.email}`
              : ''}
          </Meta>
          <Meta label={t('pages.zoom.recordings.columns.duration')}>
            {recording.durationMinutes} {t('pages.zoom.recordings.min')}
          </Meta>
          <Meta label={t('pages.zoom.recordings.columns.size')}>
            {formatBytes(recording.totalSizeBytes)}
          </Meta>
          <Meta label="Meeting ID">{recording.zoomMeetingId}</Meta>
          <Meta label="UUID">{recording.zoomMeetingUuid}</Meta>
        </MetaGrid>
      </Panel>

      <Panel>
        <PanelTitle>{t('pages.zoom.recordings.filesTitle')}</PanelTitle>
        {files.length ? (
          <FileList>
            {files.map((file, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <FileRow key={`${file.file_type}-${index}`}>
                <FileChip>{(file.file_type || '?').toUpperCase()}</FileChip>
                {file.recording_type ? (
                  <FileType>{file.recording_type}</FileType>
                ) : null}
              </FileRow>
            ))}
          </FileList>
        ) : (
          <Muted>{t('pages.zoom.recordings.noFiles')}</Muted>
        )}
      </Panel>
    </Wrapper>
  );
});

interface IMetaProps {
  label: string;
  children: React.ReactNode;
}

const Meta = ({ label, children }: IMetaProps) => (
  <MetaItem>
    <MetaLabel>{label}</MetaLabel>
    <MetaValue>{children}</MetaValue>
  </MetaItem>
);

const Wrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px 0 40px;
`;

const BackLink = styled.button`
  border: none;
  background: none;
  color: ${Colors.Blue};
  cursor: pointer;
  padding: 0;
  font-size: 14px;
`;

const Panel = styled.div`
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 2px ${Colors.BlueShadow};
  margin-bottom: 20px;
`;

const HeaderPanel = styled(Panel)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
`;

const Title = styled.h2`
  ${getFont({ fontSize: 20, lineHeight: 26, familyWeight: 'bold' })};
  color: ${Colors.Black};
  margin: 0;
`;

const SubTitle = styled.div`
  font-size: 14px;
  color: ${Colors.Gray};
`;

const StateChip = styled.span<{ state: RecordingState }>`
  align-self: flex-start;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ state }) => STATE_COLORS[state].fg};
  background: ${({ state }) => STATE_COLORS[state].bg};
`;

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TooShortChip = styled.span`
  align-self: flex-start;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  background: #fef3c7;
`;

const PanelTitle = styled.h3`
  ${getFont({ fontSize: 14, lineHeight: 18, familyWeight: 'semibold' })};
  color: ${Colors.Black};
  margin: 0 0 14px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const MetaValue = styled.span`
  font-size: 14px;
  color: ${Colors.Black};
  word-break: break-word;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FileChip = styled.span`
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.Black};
  background: ${Colors.LightGray};
`;

const FileType = styled.span`
  font-size: 13px;
  color: ${Colors.Gray};
`;

const Muted = styled.span`
  color: ${Colors.Gray};
  font-size: 14px;
`;
